/**
 * @velora/tokens. Owner: WP0-DS (BUILD-PLAN 3.2).
 *
 * Import `@velora/tokens/theme.css` once at the app root (it pulls in the
 * self-hosted fonts). Components reference tokens only through CSS custom
 * properties, never raw colour values. The CSS is generated from this
 * package's OKLCH generator (src/css.ts); `pnpm --filter @velora/tokens
 * generate` rewrites it and `contrast` runs the contrast CI on its own.
 */
import { DEFAULT_DENSITY, DENSITIES, type Density } from './scale.js';
import { CLINICAL_PRESETS, type ClinicalPreset } from './clinical.js';
import { DEFAULT_SKIN, type SkinId } from './skins/index.js';

export const THEMES = ['dark', 'light'] as const;
export type Theme = (typeof THEMES)[number];

/** Dark is the house default (ADR-0007). */
export const DEFAULT_THEME: Theme = 'dark';

export const CONTRASTS = ['standard', 'high'] as const;
export type Contrast = (typeof CONTRASTS)[number];

export const DEFAULT_CLINICAL_PRESET: ClinicalPreset = 'velora';

export function isTheme(value: unknown): value is Theme {
  return typeof value === 'string' && (THEMES as readonly string[]).includes(value);
}

export function isContrast(value: unknown): value is Contrast {
  return typeof value === 'string' && (CONTRASTS as readonly string[]).includes(value);
}

export function isDensity(value: unknown): value is Density {
  return typeof value === 'string' && (DENSITIES as readonly string[]).includes(value);
}

export function isClinicalPreset(value: unknown): value is ClinicalPreset {
  return typeof value === 'string' && (CLINICAL_PRESETS as readonly string[]).includes(value);
}

/** Display appearance, applied as data attributes on one element (usually <html>). */
export interface Appearance {
  theme: Theme;
  /** User-selected skin (`data-skin`); omitted means Velora. */
  skin?: SkinId;
  contrast: Contrast;
  density: Density;
  clinical: ClinicalPreset;
  reducedMotion: boolean;
  /** Performance mode: no backdrop blur, no animated backgrounds (BUILD-PLAN 3.1 rule 6). */
  performance: boolean;
}

export const DEFAULT_APPEARANCE: Appearance = {
  theme: DEFAULT_THEME,
  contrast: 'standard',
  density: DEFAULT_DENSITY,
  clinical: DEFAULT_CLINICAL_PRESET,
  reducedMotion: false,
  performance: false,
};

/** The data attributes theme.css keys on; `undefined` means "remove the attribute". */
export function appearanceAttributes(a: Appearance): Record<string, string | undefined> {
  return {
    'data-theme': a.theme,
    'data-skin': a.skin ?? DEFAULT_SKIN,
    'data-contrast': a.contrast === 'high' ? 'high' : undefined,
    'data-density': a.density,
    'data-clin': a.clinical,
    'data-motion': a.reducedMotion ? 'reduced' : undefined,
    'data-performance': a.performance ? 'on' : undefined,
  };
}

/** `var(--name)` helper so TS code never spells raw colours. */
export function token(name: string): string {
  return `var(--${name})`;
}

export { DENSITIES, DEFAULT_DENSITY, DENSITY_TOKENS, type Density } from './scale.js';
export {
  CLINICAL_LABELS,
  CLINICAL_PAINT,
  CLINICAL_PRESETS,
  CLINICAL_ROLES,
  type ClinicalPaint,
  type ClinicalPreset,
  type ClinicalRole,
} from './clinical.js';
export { CHART_COUNT, STATUS_TONES, type StatusTone } from './theme.js';
export {
  DEFAULT_SKIN,
  defineSkinTokens,
  isSkinId,
  SKIN_ATTR,
  SKIN_IDS,
  SKIN_ROLE_PREFIX,
  skinRoleNames,
  skinTokenNames,
  skinTokensCss,
  type SkinId,
  type SkinRoleTokens,
  type SkinTokens,
  type TokenMap,
} from './skins/index.js';
export { APPT_STATUS_ABBR_COLORS, APPT_STATUS_COLORS } from './apptStatus.js';
